/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/* ============================================================================
   LINEUP BY FAMILY 2026
   Tabs filter (Apple-style pill with sliding indicator) + stagger card reveal
   on filter change. Scoped per section via [data-section-id].
   ============================================================================ */

(function () {
  "use strict";

  async function loadCollectionFamilies(root) {
    if (!root.hasAttribute("data-family-collection") || root._familyLoad) return;
    var events = new AbortController();
    root._familyLoad = events;
    var wrapper = root.closest(".shopify-section") || root.parentElement;
    var pageCount = Number(root.dataset.familyPages) || 1;
    var currentPage = Number(root.dataset.familyPage) || 1;
    if (pageCount === 1 && currentPage === 1) return;
    root.setAttribute("aria-busy", "true");
    try {
      var pages = new Map([[currentPage, Array.from(root.querySelectorAll("[data-family-key]"))]]);
      for (var page = 1; page <= pageCount; page++) {
        if (page === currentPage) continue;
        var url = new URL(window.location.href);
        url.searchParams.set("section_id", root.dataset.sectionId);
        url.searchParams.set("page", String(page));
        var response = await fetch(url, { signal: events.signal });
        if (!response.ok) throw new Error("Collection page unavailable");
        var doc = new DOMParser().parseFromString(await response.text(), "text/html");
        var nextRoot = doc.querySelector("[data-family-collection]");
        if (!nextRoot) throw new Error("Collection markup unavailable");
        pages.set(page, Array.from(nextRoot.querySelectorAll("[data-family-key]")));
      }
      if (events.signal.aborted || !root.isConnected) return;
      var families = new Map();
      Array.from(pages.keys()).sort(function (a, b) { return a - b; }).forEach(function (page) {
        pages.get(page).forEach(function (card) {
          var key = card.dataset.familyKey;
          var family = families.get(key);
          if (!family) {
            family = { card: card, sizes: new Set(), constructions: new Map() };
            families.set(key, family);
          }
          if (Number(card.dataset.familyPrice) < Number(family.card.dataset.familyPrice)) family.card = card;
          var sizes = card.querySelector(".family-card__sizes");
          (sizes ? sizes.textContent.split(",") : []).forEach(function (size) {
            if (size.trim()) family.sizes.add(size.trim());
          });
          card.querySelectorAll(".family-card__constructions > *").forEach(function (chip) {
            family.constructions.set(chip.textContent.trim(), chip.cloneNode(true));
          });
        });
      });
      var cards = [];
      families.forEach(function (family) {
        var card = document.importNode(family.card, true);
        card.classList.remove("scroll-trigger", "animate--slide-in");
        var sizes = card.querySelector(".family-card__sizes");
        if (family.sizes.size) {
          if (!sizes) {
            sizes = document.createElement("div");
            sizes.className = "family-card__sizes";
            card.querySelector(".family-card__meta").appendChild(sizes);
          }
          // Ascending volumes; non-numeric labels (155D, M) keep their order after the numbers
          sizes.textContent = Array.from(family.sizes)
            .map(function (size, index) { return { size: size, index: index, value: /^\d+(\.\d+)?L?$/.test(size) ? parseFloat(size) : Infinity }; })
            .sort(function (a, b) { return a.value - b.value || a.index - b.index; })
            .map(function (entry) { return entry.size; })
            .join(", ");
        }
        var constructions = card.querySelector(".family-card__constructions");
        if (family.constructions.size) {
          if (!constructions) {
            constructions = document.createElement("div");
            constructions.className = "family-card__constructions";
            card.querySelector(".family-card__media").appendChild(constructions);
          }
          constructions.replaceChildren.apply(constructions, Array.from(family.constructions.values()));
        }
        cards.push(card);
      });
      root.querySelector(".lineup__grid").replaceChildren.apply(root.querySelector(".lineup__grid"), cards);
      window.dispatchEvent(new Event("resize"));
      root.querySelectorAll("img").forEach(function (image) {
        if (!image.complete) image.addEventListener("load", function () { window.dispatchEvent(new Event("resize")); }, { once: true, signal: events.signal });
      });
      var count = root.querySelector("[data-family-model-count]");
      if (count) count.textContent = root.dataset.familyModelsLabel.replace("__COUNT__", cards.length);
      if (root.dataset.familySingle === "true" && cards.length <= 1) root.hidden = true;
      var nav = wrapper.querySelector("[data-family-pagination-nav]");
      if (nav) nav.hidden = true;
      var jsonld = wrapper.querySelector("[data-family-jsonld]");
      if (jsonld) {
        var data = JSON.parse(jsonld.textContent);
        data.numberOfItems = cards.length;
        data.itemListElement = cards.map(function (card, index) {
          return { "@type": "ListItem", position: index + 1, item: { "@type": "Product", url: card.href, name: card.querySelector(".family-card__name")?.textContent.trim() || "", offers: { "@type": "AggregateOffer", priceCurrency: card.dataset.familyCurrency, lowPrice: Number(card.dataset.familyPrice) / 100 } } };
        });
        jsonld.textContent = JSON.stringify(data);
      }
    } catch (error) {
      if (events.signal.aborted) return;
      var status = document.createElement("p");
      status.setAttribute("role", "status");
      status.textContent = root.dataset.familyError;
      root.appendChild(status);
    } finally {
      root.removeAttribute("aria-busy");
    }
  }

  function initLineup(root) {
    if (!root || root.dataset.lineupInited === "1") return;
    root.dataset.lineupInited = "1";
    loadCollectionFamilies(root);

    var tabsEl = root.querySelector(".lineup__tabs");
    var tabs = tabsEl ? tabsEl.querySelectorAll(".lineup__tab") : [];
    var indicator = tabsEl ? tabsEl.querySelector(".lineup__tab-indicator") : null;
    var grid = root.querySelector(".lineup__grid");
    var cards = root.querySelectorAll(".family-card");
    if (!tabs.length || !cards.length) return;

    var staggerTimeout = null;
    var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function positionIndicator(tab) {
      if (!indicator) return;
      indicator.style.left = tab.offsetLeft + "px";
      indicator.style.width = tab.offsetWidth + "px";
    }

    function activate(tab, animate) {
      var filter = tab.getAttribute("data-filter");
      tabs.forEach(function (t) {
        t.setAttribute("aria-pressed", "false");
      });
      tab.setAttribute("aria-pressed", "true");
      positionIndicator(tab);

      cards.forEach(function (card) {
        var d = card.getAttribute("data-discipline");
        var show = filter === "all" || d === filter;
        card.classList.toggle("is-hidden", !show);
      });

      if (animate && !prefersReducedMotion && grid) {
        grid.classList.remove("is-filtering");
        void grid.offsetWidth;
        grid.classList.add("is-filtering");
        clearTimeout(staggerTimeout);
        staggerTimeout = setTimeout(function () {
          grid.classList.remove("is-filtering");
        }, 700);
      }
    }

    tabs.forEach(function (t) {
      t.addEventListener("click", function () {
        activate(t, true);
      });
    });

    var initial = tabsEl.querySelector('.lineup__tab[aria-pressed="true"]') || tabs[0];
    if (indicator) indicator.style.transition = "none";
    activate(initial, false);
    requestAnimationFrame(function () {
      if (indicator) indicator.style.transition = "";
    });

    // Debounced resize via rAF, listener stored on root for cleanup
    var resizeRaf = 0;
    function onResize() {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        if (!root.isConnected) return;
        var active = tabsEl.querySelector('.lineup__tab[aria-pressed="true"]');
        if (!active || !indicator) return;
        var prev = indicator.style.transition;
        indicator.style.transition = "none";
        positionIndicator(active);
        requestAnimationFrame(function () {
          indicator.style.transition = prev;
        });
      });
    }
    window.addEventListener("resize", onResize);
    root._lineupResizeHandler = onResize;
  }

  function teardownLineup(root) {
    root?._familyLoad?.abort();
    if (!root) return;
    delete root._familyLoad;
    delete root.dataset.lineupInited;
    if (!root._lineupResizeHandler) return;
    window.removeEventListener("resize", root._lineupResizeHandler);
    root._lineupResizeHandler = null;
    delete root.dataset.lineupInited;
  }

  function initAll() {
    document.querySelectorAll(".lineup[data-section-id]").forEach(initLineup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init on section load/reload, teardown on unload
  function sectionRootFromEvent(e) {
    return e.target && e.target.querySelector ? e.target.querySelector(".lineup") : null;
  }

  document.addEventListener("shopify:section:load", function (e) {
    var root = sectionRootFromEvent(e);
    if (root) initLineup(root);
  });

  document.addEventListener("shopify:section:reload", function (e) {
    var root = sectionRootFromEvent(e);
    if (root) initLineup(root);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var root = sectionRootFromEvent(e);
    if (root) teardownLineup(root);
  });
})();
